import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useBreakpoint } from "../hooks/useBreakpoint";

/** Landing comercial pública de HelixScan — v5 "liquid glass".
 *
 *  Dirección de diseño: lenguaje Apple 2025/26 (Liquid Glass). Composición
 *  centrada con titulares grandes de tracking apretado (Inter ≈ SF Pro), nav
 *  flotante de cristal, paneles translúcidos con blur+saturación, borde
 *  especular y highlight interior, resplandores de color ESTÁTICOS detrás del
 *  cristal (el vidrio necesita algo que refractar), botones píldora y enlaces
 *  "Más información ›". Coherente con la app (que ya usa glassmorphism, §5).
 *  Sin gradiente de texto animado, sin tilt 3D, sin contadores, sin marquee.
 *  Movimiento mínimo (fade-in al scroll). Respeta prefers-reduced-motion.
 */

const INK = "#0b1215";                       // casi negro (texto)
const MUTED = "rgba(11,18,21,0.62)";
const FAINT = "rgba(11,18,21,0.42)";
const TEAL = "#0891b2";                      // teal HelixScan
const TEAL_DARK = "#0a7a96";
const VIOLET = "#7c3aed";                    // violeta del logo (solo en glows)
const BG = "#f4f6f7";                        // lienzo claro frío
const DARK = "#0a0f12";                      // sección oscura (casi negro Apple)
const SANS = "'Inter', -apple-system, 'SF Pro Display', system-ui, sans-serif";

const MAILTO = "mailto:luis@haltrixpartners.com"
  + "?subject=" + encodeURIComponent("Solicitud de reunión — HelixScan")
  + "&body=" + encodeURIComponent(
    "Hola,\n\nMe gustaría agendar una reunión para conocer HelixScan.\n\n"
    + "Laboratorio:\nNombre y cargo:\nDisponibilidad:\n");

/* ── CSS global de la landing ───────────────────────────────────────────── */
const CSS = `
  .lp * { box-sizing: border-box; }
  .lp { scroll-behavior: smooth; font-variant-numeric: tabular-nums;
        -webkit-font-smoothing: antialiased; }
  .lp ::selection { background: ${TEAL}; color: #fff; }

  .lp-reveal { opacity: 0; transform: translateY(14px);
               transition: opacity 0.6s cubic-bezier(.25,.6,.3,1), transform 0.6s cubic-bezier(.25,.6,.3,1); }
  .lp-reveal.lp-in { opacity: 1; transform: none; }

  @keyframes lpRowIn { from { opacity: 0; } to { opacity: 1; } }
  @keyframes lpDot { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes lpModalIn { from { opacity: 0; transform: translateY(12px) scale(0.99); } to { opacity: 1; transform: none; } }
  @keyframes lpFadeIn  { from { opacity: 0; } to { opacity: 1; } }

  /* ── Material: vidrio líquido ── */
  .lp-glass {
    background: rgba(255,255,255,0.55);
    -webkit-backdrop-filter: blur(24px) saturate(1.8);
    backdrop-filter: blur(24px) saturate(1.8);
    border: 1px solid rgba(255,255,255,0.75);
    box-shadow: 0 8px 40px -12px rgba(11,40,50,0.14),
                inset 0 1px 0 rgba(255,255,255,0.9);
  }
  .lp-glass-strong {
    background: rgba(255,255,255,0.68);
    -webkit-backdrop-filter: blur(28px) saturate(1.9);
    backdrop-filter: blur(28px) saturate(1.9);
    border: 1px solid rgba(255,255,255,0.85);
    box-shadow: 0 24px 80px -24px rgba(11,40,50,0.22),
                inset 0 1px 0 rgba(255,255,255,0.95);
  }
  .lp-glass-dark {
    background: rgba(255,255,255,0.07);
    -webkit-backdrop-filter: blur(24px) saturate(1.6);
    backdrop-filter: blur(24px) saturate(1.6);
    border: 1px solid rgba(255,255,255,0.14);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.12);
  }
  @supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    .lp-glass, .lp-glass-strong { background: rgba(255,255,255,0.92); }
    .lp-glass-dark { background: rgba(30,40,45,0.85); }
  }

  .lp-card { border-radius: 24px; transition: transform 0.35s cubic-bezier(.25,.6,.3,1), box-shadow 0.35s; }
  .lp-card:hover { transform: translateY(-3px);
    box-shadow: 0 20px 60px -20px rgba(11,40,50,0.2), inset 0 1px 0 rgba(255,255,255,0.9); }

  /* ── Botones (píldoras Apple) ── */
  .lp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border-radius: 999px; text-decoration: none; cursor: pointer; border: 0;
    font-family: ${SANS}; font-weight: 600; line-height: 1;
    transition: transform 0.2s cubic-bezier(.25,.6,.3,1), background 0.2s, box-shadow 0.2s; }
  .lp-btn:active { transform: scale(0.98); }
  .lp-btn-primary { background: ${TEAL}; color: #fff; padding: 16px 30px; font-size: 16px;
    box-shadow: 0 6px 24px -8px rgba(8,145,178,0.5); }
  .lp-btn-primary:hover { background: ${TEAL_DARK}; transform: scale(1.03); }
  .lp-btn-glass { color: ${INK}; padding: 16px 28px; font-size: 16px;
    background: rgba(255,255,255,0.55);
    -webkit-backdrop-filter: blur(20px) saturate(1.8);
    backdrop-filter: blur(20px) saturate(1.8);
    border: 1px solid rgba(255,255,255,0.8);
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.9), 0 4px 18px -8px rgba(11,40,50,0.15); }
  .lp-btn-glass:hover { background: rgba(255,255,255,0.75); transform: scale(1.03); }

  .lp-link { color: ${TEAL}; text-decoration: none; font-weight: 600; font-size: 16px;
    display: inline-flex; align-items: center; gap: 4px; }
  .lp-link:hover { text-decoration: underline; }

  .lp-navlink { color: rgba(11,18,21,0.72); text-decoration: none; font-size: 13.5px; font-weight: 500;
    transition: color 0.15s ease; }
  .lp-navlink:hover { color: ${INK}; }

  .lp-input {
    width: 100%; padding: 13px 15px; border-radius: 12px; font-size: 15px;
    font-family: ${SANS}; color: ${INK}; background: rgba(255,255,255,0.8);
    border: 1px solid rgba(11,18,21,0.14); outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .lp-input:focus { border-color: ${TEAL}; background: #fff;
    box-shadow: 0 0 0 4px rgba(8,145,178,0.14); }

  @media (prefers-reduced-motion: reduce) {
    .lp *, .lp *::before, .lp *::after { animation: none !important; transition: none !important; }
    .lp-reveal { opacity: 1; transform: none; }
  }
`;

/* ── Scroll-reveal sutil ────────────────────────────────────────────────── */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".lp-reveal");
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add("lp-in"); io.unobserve(e.target); }
      }),
      { threshold: 0.15 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* ── Resplandor ambiente estático (lo que el cristal refracta) ──────────── */
function Glow({ top, left, size, color, opacity }: {
  top: string; left: string; size: number; color: string; opacity: number;
}) {
  return (
    <div aria-hidden style={{
      position: "absolute", top, left, width: size, height: size,
      transform: "translate(-50%, -50%)",
      background: `radial-gradient(circle closest-side, ${color}, transparent 70%)`,
      opacity, filter: "blur(60px)", pointerEvents: "none",
    }} />
  );
}

/* ── Iconos de línea (estilo SF Symbols) ────────────────────────────────── */
const Ic = ({ d, size = 22, color = TEAL }: { d: string; size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d={d} />
  </svg>
);
const PATHS = {
  bell: "M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0",
  radar: "M9.348 14.651a3.75 3.75 0 010-5.303m5.304 0a3.75 3.75 0 010 5.303m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894c-3.808-3.808-3.808-9.98 0-13.789m13.788 0c3.808 3.808 3.808 9.981 0 13.79M12 12h.008v.007H12V12z",
  scale: "M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-4.062 0c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-13.5 0L2.63 15.696c-.122.499.106 1.028.589 1.202a5.989 5.989 0 004.062 0c.483-.174.711-.703.59-1.202L5.25 4.971z",
  doc: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m6.75 12l-3 3m0 0l-3-3m3 3v-6m-1.5-9H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
  trend: "M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941",
  users: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
  check: "M4.5 12.75l6 6 9-13.5",
};

/* ── Registro de detecciones (mock del producto en vidrio) ──────────────── */
const FEED = [
  { time: "09:41", tag: "PORTFOLIO",  tagColor: TEAL,      urgent: true,
    name: "Exclusión de financiación — OLMETEC 40 mg", source: "AEMPS" },
  { time: "09:12", tag: "COMPETIDOR", tagColor: "#b45309", urgent: false,
    name: "Bajada de precio −12 % — Olmesartán Cinfa", source: "SPRAH" },
  { time: "08:47", tag: "PORTFOLIO",  tagColor: TEAL,      urgent: false,
    name: "Nueva financiación — LIXIANA 15 mg", source: "Nomenclátor" },
  { time: "08:15", tag: "RADAR",      tagColor: VIOLET,    urgent: false,
    name: "CMH autoriza 14 genéricos nuevos", source: "AEMPS" },
  { time: "07:58", tag: "COMPETIDOR", tagColor: "#b45309", urgent: false,
    name: "Deducción 7,5 % — Perindopril Teva", source: "SNS" },
  { time: "07:31", tag: "PORTFOLIO",  tagColor: TEAL,      urgent: false,
    name: "Precio menor actualizado — COVERSYL 4 mg", source: "PMAH" },
];

function DetectionGlass({ isMobile }: { isMobile: boolean }) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setOffset(o => (o + 1) % FEED.length), 5000);
    return () => clearInterval(id);
  }, []);
  const rows = Array.from({ length: 4 }, (_, i) => FEED[(offset + i) % FEED.length]);

  return (
    <div className="lp-glass-strong" style={{
      width: "100%", maxWidth: 720, margin: "0 auto",
      borderRadius: 28, overflow: "hidden",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: isMobile ? "14px 18px" : "16px 26px",
        borderBottom: "1px solid rgba(11,18,21,0.07)",
      }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ display: "inline-flex", gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 5, background: "rgba(11,18,21,0.12)" }} />
            <span style={{ width: 10, height: 10, borderRadius: 5, background: "rgba(11,18,21,0.12)" }} />
            <span style={{ width: 10, height: 10, borderRadius: 5, background: "rgba(11,18,21,0.12)" }} />
          </span>
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.14em", color: MUTED, textTransform: "uppercase" }}>
          Detecciones de hoy
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 7,
                       fontSize: 11, fontWeight: 600, letterSpacing: "0.1em",
                       color: TEAL, textTransform: "uppercase" }}>
          <span style={{ width: 7, height: 7, borderRadius: 4, background: TEAL, animation: "lpDot 2.4s ease-in-out infinite" }} />
          En vivo
        </span>
      </div>
      {rows.map((r, i) => (
        <div key={`${r.name}-${offset}`} style={{
          display: "flex", alignItems: "flex-start", gap: isMobile ? 12 : 18,
          padding: isMobile ? "14px 18px" : "16px 26px",
          borderBottom: "1px solid rgba(11,18,21,0.06)",
          animation: i === 0 ? "lpRowIn 0.6s ease" : undefined,
          background: r.urgent && i === 0 ? "rgba(8,145,178,0.06)" : "transparent",
        }}>
          <span style={{ fontSize: 12.5, color: FAINT, paddingTop: 3, width: 42, flexShrink: 0 }}>{r.time}</span>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, textAlign: "left" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: r.tagColor }}>{r.tag}</span>
              {r.urgent && i === 0 && (
                <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", color: "#dc2626" }}>
                  ALERTA ENVIADA
                </span>
              )}
            </div>
            <span style={{ fontSize: isMobile ? 14.5 : 15.5, fontWeight: 600, color: INK, lineHeight: 1.35 }}>{r.name}</span>
            <span style={{ fontSize: 12.5, color: FAINT }}>Fuente oficial: {r.source}</span>
          </div>
        </div>
      ))}
      <div style={{
        padding: isMobile ? "12px 18px" : "13px 26px",
        display: "flex", justifyContent: "space-between", fontSize: 12.5, color: FAINT,
      }}>
        <span>72.447 cambios desde 2010</span>
        <span>trazable al documento original</span>
      </div>
    </div>
  );
}

/* ── Datos de secciones ─────────────────────────────────────────────────── */
const FEATURES = [
  { icon: PATHS.bell, title: "Alertas críticas en minutos",
    body: "Exclusión de financiación o alerta de seguridad sobre un producto tuyo → email urgente al equipo en el momento de publicarse." },
  { icon: PATHS.radar, title: "Radar regulatorio",
    body: "CMH, bajadas voluntarias, nuevas financiaciones y BOE: el mes regulatorio completo en una pantalla, listo para tu comité." },
  { icon: PATHS.scale, title: "Tu precio vs. competencia",
    body: "El PVL de cada producto frente a todos los competidores de su molécula, sobre 15 años de histórico oficial del SNS." },
  { icon: PATHS.doc, title: "Informe mensual ejecutivo",
    body: "Un PDF con el membrete de tu laboratorio, listo para reenviar a dirección. La plataforma se presenta sola cada mes." },
  { icon: PATHS.trend, title: "Predicciones de precio",
    body: "Modelos entrenados con 15 años de datos estiman riesgo de caída y oportunidades por producto. De reaccionar a anticiparse." },
  { icon: PATHS.users, title: "Trabajo en equipo",
    body: "Marcas compartidas, notas en cada cambio, exportación a Excel y resumen semanal. Lo que uno revisa, todos lo ven." },
];

const PLANS = [
  {
    id: "Core", tagline: "La monitorización esencial", pro: false,
    features: ["Monitorización de 11 fuentes oficiales", "Cambios, histórico desde 2010 y búsqueda",
      "Radar regulatorio mensual", "Portfolio y competidores ilimitados",
      "Informe mensual ejecutivo en PDF", "Hasta 5 usuarios"],
  },
  {
    id: "Pro", tagline: "Para equipos que no pueden llegar tarde", pro: true,
    features: ["Todo lo de Core", "Alertas por email en tiempo real",
      "Alertas críticas urgentes (exclusiones y seguridad)", "Resumen semanal personalizado",
      "Hasta 10 usuarios"],
  },
  {
    id: "Ultra", tagline: "Inteligencia competitiva completa", pro: false,
    features: ["Todo lo de Pro", "Comparador de precios vs. competencia",
      "Predicciones de precio", "Ranking de oportunidades",
      "Hasta 20 usuarios"],
  },
];

const SOURCES = [
  "AEMPS · CIMA", "AEMPS · Alertas", "AEMPS · CMH", "BOE",
  "SNS · Precios menores", "SNS · Genéricos", "SNS · Deducciones",
  "SPRAH", "CIPM", "EMA", "Nomenclátor RX", "Nomenclátor FC",
];

/* ── Modal de solicitud de reunión ──────────────────────────────────────── */
function ContactModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [challenge, setChallenge] = useState<{ token: string; question: string } | null>(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", company: "", message: "", answer: "" });
  const [honeypot, setHoneypot] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSent(false); setError(null);
    fetch("/api/public/contact-challenge")
      .then(r => r.json()).then(setChallenge)
      .catch(() => setError("No se pudo cargar el formulario. Inténtalo de nuevo."));
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge || sending) return;
    setSending(true); setError(null);
    try {
      const r = await fetch("/api/public/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, token: challenge.token, website: honeypot }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "No se pudo enviar la solicitud.");
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo enviar la solicitud.");
      fetch("/api/public/contact-challenge").then(r => r.json()).then(setChallenge).catch(() => {});
    } finally {
      setSending(false);
    }
  }

  const label: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: MUTED, marginBottom: 6, display: "block" };

  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 500, display: "flex", alignItems: "center",
      justifyContent: "center", padding: 16,
      background: "rgba(11,18,21,0.35)",
      WebkitBackdropFilter: "blur(14px)", backdropFilter: "blur(14px)",
      animation: "lpFadeIn 0.22s ease",
    }}>
      <div onClick={e => e.stopPropagation()} className="lp-glass-strong" style={{
        width: "min(520px, 100%)", maxHeight: "92vh", overflowY: "auto",
        background: "rgba(255,255,255,0.88)",
        borderRadius: 28, padding: "34px 32px",
        animation: "lpModalIn 0.3s cubic-bezier(.25,.6,.3,1)",
        position: "relative", fontFamily: SANS,
      }}>
        <button onClick={onClose} aria-label="Cerrar" style={{
          position: "absolute", top: 16, right: 16, width: 34, height: 34,
          borderRadius: 17, border: "1px solid rgba(11,18,21,0.1)", background: "rgba(255,255,255,0.8)",
          color: MUTED, fontSize: 14, cursor: "pointer", lineHeight: 1,
        }}>✕</button>

        {sent ? (
          <div style={{ textAlign: "center", padding: "26px 4px" }}>
            <div style={{
              width: 56, height: 56, borderRadius: 28, margin: "0 auto 18px",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "rgba(8,145,178,0.1)", border: "1px solid rgba(8,145,178,0.25)",
            }}><Ic d={PATHS.check} size={26} /></div>
            <h3 style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: INK, margin: "0 0 10px" }}>
              Solicitud enviada
            </h3>
            <p style={{ fontSize: 15, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              Gracias, {form.first_name}. Te contactaremos en <strong>menos de 24 horas</strong> para
              agendar la reunión.
            </p>
            <button className="lp-btn lp-btn-primary" onClick={onClose} style={{ marginTop: 24, padding: "13px 26px", fontSize: 15 }}>
              Cerrar
            </button>
          </div>
        ) : (
          <>
            <h3 style={{ fontSize: 25, fontWeight: 700, letterSpacing: "-0.02em", color: INK, margin: "0 0 8px" }}>
              Solicitar una reunión
            </h3>
            <p style={{ fontSize: 14.5, color: MUTED, margin: "0 0 24px", lineHeight: 1.55 }}>
              Cuéntanos quién eres y te escribimos en menos de 24 h para agendar 30 minutos.
            </p>
            <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label style={label}>Nombre</label>
                  <input className="lp-input" required minLength={2} value={form.first_name} onChange={set("first_name")} placeholder="María" />
                </div>
                <div>
                  <label style={label}>Apellidos</label>
                  <input className="lp-input" required minLength={2} value={form.last_name} onChange={set("last_name")} placeholder="García" />
                </div>
              </div>
              <div>
                <label style={label}>Email profesional</label>
                <input className="lp-input" required type="email" value={form.email} onChange={set("email")} placeholder="maria.garcia@laboratorio.es" />
              </div>
              <div>
                <label style={label}>Empresa</label>
                <input className="lp-input" required minLength={2} value={form.company} onChange={set("company")} placeholder="Laboratorio / consultora" />
              </div>
              <div>
                <label style={label}>Mensaje <span style={{ fontWeight: 400, color: FAINT }}>(opcional)</span></label>
                <textarea className="lp-input" rows={3} maxLength={2000} value={form.message} onChange={set("message")}
                          placeholder="Cuéntanos qué te gustaría ver en la reunión…" style={{ resize: "vertical" }} />
              </div>
              {/* Honeypot anti-bots: invisible para humanos */}
              <div aria-hidden style={{ position: "absolute", left: -9999, top: -9999, height: 0, overflow: "hidden" }}>
                <label>Website
                  <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={e => setHoneypot(e.target.value)} />
                </label>
              </div>
              <div>
                <label style={label}>Verificación: ¿cuánto es {challenge?.question ?? "…"}?</label>
                <input className="lp-input" required inputMode="numeric" value={form.answer} onChange={set("answer")}
                       placeholder="Tu respuesta" style={{ maxWidth: 180 }} />
              </div>
              {error && (
                <div style={{
                  background: "rgba(220,38,38,0.07)", border: "1px solid rgba(220,38,38,0.25)",
                  color: "#b91c1c", borderRadius: 12, padding: "10px 14px", fontSize: 13.5,
                }}>{error}</div>
              )}
              <button type="submit" className="lp-btn lp-btn-primary" disabled={sending || !challenge}
                      style={{ opacity: sending ? 0.7 : 1 }}>
                {sending ? "Enviando…" : "Enviar solicitud"}
              </button>
              <p style={{ fontSize: 12, color: FAINT, margin: 0, textAlign: "center" }}>
                Usaremos tus datos únicamente para responder a esta solicitud.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Página ─────────────────────────────────────────────────────────────── */
export default function LandingPage() {
  const { isMobile, isTablet } = useBreakpoint();
  const narrow = isMobile || isTablet;
  // #contacto abre el formulario directamente (enlazable desde campañas/emails)
  const [contactOpen, setContactOpen] = useState(() =>
    typeof window !== "undefined" && window.location.hash === "#contacto");
  useReveal();

  useEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = BG;
    return () => { document.body.style.background = prev; };
  }, []);

  const container: React.CSSProperties = {
    maxWidth: 1120, margin: "0 auto", padding: isMobile ? "0 20px" : "0 40px",
  };
  const h2: React.CSSProperties = {
    fontSize: isMobile ? 34 : 52, fontWeight: 700, letterSpacing: "-0.03em",
    lineHeight: 1.08, margin: 0, color: INK,
  };
  const sectionSub: React.CSSProperties = {
    fontSize: isMobile ? 16 : 18.5, color: MUTED, lineHeight: 1.6,
    maxWidth: 560, margin: "20px auto 0",
  };

  return (
    <div className="lp" style={{ background: BG, color: INK, fontFamily: SANS, overflowX: "clip" }}>
      <style>{CSS}</style>

      {/* ── Nav flotante de cristal ─────────────────────────────────────── */}
      <header style={{ position: "sticky", top: 12, zIndex: 100, padding: "0 16px" }}>
        <div className="lp-glass" style={{
          maxWidth: 980, margin: "0 auto", borderRadius: 999,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          height: 58, padding: "0 10px 0 22px",
        }}>
          <Link to="/" aria-label="HelixScan — inicio" style={{ display: "flex", alignItems: "center" }}>
            <img src="/helixscan-logo.png" alt="HelixScan" style={{ height: 30, display: "block" }} />
          </Link>
          <nav style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 26 }}>
            {!narrow && (
              <>
                <a className="lp-navlink" href="#producto">Plataforma</a>
                <a className="lp-navlink" href="#fuentes">Fuentes</a>
                <a className="lp-navlink" href="#planes">Planes</a>
              </>
            )}
            <Link to="/login" className="lp-navlink" style={{ color: INK, fontWeight: 600 }}>Acceder</Link>
            <button className="lp-btn lp-btn-primary" style={{ padding: "10px 18px", fontSize: 13.5 }}
                    onClick={() => setContactOpen(true)}>
              {isMobile ? "Reunión" : "Solicitar reunión"}
            </button>
          </nav>
        </div>
      </header>

      {/* ── Hero (centrado, Apple) ──────────────────────────────────────── */}
      <section style={{ position: "relative", overflow: "hidden" }}>
        {/* Resplandores estáticos que el cristal refracta */}
        <Glow top="18%" left="22%" size={640} color="rgba(8,145,178,0.34)" opacity={0.8} />
        <Glow top="30%" left="82%" size={560} color="rgba(124,58,237,0.22)" opacity={0.7} />
        <Glow top="78%" left="55%" size={700} color="rgba(6,182,212,0.2)" opacity={0.7} />

        <div style={{ ...container, position: "relative", padding: isMobile ? "68px 20px 72px" : "104px 40px 96px", textAlign: "center" }}>
          <div className="lp-reveal lp-in" style={{
            fontSize: 13, fontWeight: 600, letterSpacing: "0.14em",
            textTransform: "uppercase", color: TEAL, marginBottom: 26,
          }}>
            Monitorización del mercado farmacéutico español
          </div>

          <h1 className="lp-reveal lp-in" style={{
            fontSize: isMobile ? 46 : 84, fontWeight: 700, letterSpacing: "-0.045em",
            lineHeight: 1.02, margin: "0 auto", maxWidth: 900, color: INK,
          }}>
            Entérate en minutos.<br />
            <span style={{ color: TEAL }}>No en semanas.</span>
          </h1>

          <p className="lp-reveal lp-in" style={{
            fontSize: isMobile ? 17 : 21, lineHeight: 1.55, color: MUTED,
            maxWidth: 620, margin: "28px auto 40px",
          }}>
            Cada cambio de precio, financiación o regulación que afecta a tu portfolio,
            detectado en las fuentes oficiales y entregado a tu equipo con el contexto para actuar.
          </p>

          <div className="lp-reveal lp-in" style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap", marginBottom: isMobile ? 56 : 76 }}>
            <button className="lp-btn lp-btn-primary" onClick={() => setContactOpen(true)}>
              Solicitar una reunión
            </button>
            <a className="lp-btn lp-btn-glass" href="#producto">Ver la plataforma</a>
          </div>

          <div className="lp-reveal">
            <DetectionGlass isMobile={isMobile} />
          </div>

          {/* Franja de métricas en chips de cristal */}
          <div className="lp-reveal" style={{
            display: "grid", gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
            gap: isMobile ? 12 : 16, marginTop: isMobile ? 52 : 72,
            maxWidth: 980, marginLeft: "auto", marginRight: "auto",
          }}>
            {[
              { v: "11", label: "fuentes oficiales" },
              { v: "72.447", label: "cambios desde 2010" },
              { v: "15 años", label: "de histórico del SNS" },
              { v: "30 min", label: "frecuencia de sondeo" },
            ].map((s, i) => (
              <div key={i} className="lp-glass lp-card" style={{ padding: isMobile ? "18px 14px" : "24px 20px" }}>
                <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 700, letterSpacing: "-0.03em", color: INK }}>{s.v}</div>
                <div style={{ fontSize: 13, color: MUTED, marginTop: 5, lineHeight: 1.4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Fuentes (chips de cristal) ──────────────────────────────────── */}
      <section id="fuentes" style={{ position: "relative" }}>
        <div style={{ ...container, padding: isMobile ? "56px 20px" : "88px 40px", textAlign: "center" }}>
          <div className="lp-reveal" style={{ fontSize: 12.5, fontWeight: 600, letterSpacing: "0.16em", textTransform: "uppercase", color: FAINT, marginBottom: 24 }}>
            Cobertura de fuentes oficiales — AEMPS · Ministerio de Sanidad · BOE · EMA
          </div>
          <div className="lp-reveal" style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center", maxWidth: 900, margin: "0 auto" }}>
            {SOURCES.map(s => (
              <span key={s} className="lp-glass" style={{
                borderRadius: 999, padding: "10px 20px",
                fontSize: 13.5, fontWeight: 500, color: "rgba(11,18,21,0.72)", whiteSpace: "nowrap",
              }}>{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── El problema (sección oscura Apple con cristal oscuro) ───────── */}
      <section style={{ background: DARK, position: "relative", overflow: "hidden" }}>
        <Glow top="30%" left="75%" size={700} color="rgba(8,145,178,0.35)" opacity={0.75} />
        <Glow top="85%" left="15%" size={520} color="rgba(124,58,237,0.25)" opacity={0.6} />
        <div style={{ ...container, position: "relative", padding: isMobile ? "76px 20px" : "128px 40px", textAlign: "center" }}>
          <h2 className="lp-reveal" style={{ ...h2, color: "#fff", maxWidth: 820, margin: "0 auto" }}>
            El BOE a mano. PDFs de 750 páginas.{" "}
            <span style={{ color: "rgba(255,255,255,0.45)" }}>Y la competencia moviéndose mientras tanto.</span>
          </h2>
          <p className="lp-reveal" style={{ ...sectionSub, color: "rgba(255,255,255,0.65)", maxWidth: 620 }}>
            La vigilancia manual no escala: se revisa cuando hay tiempo, y las sorpresas aparecen
            en el comité del mes siguiente — cuando ya es tarde para responder.
          </p>
          <div className="lp-reveal lp-glass-dark" style={{
            borderRadius: 24, maxWidth: 720, margin: "44px auto 0",
            padding: isMobile ? "22px 24px" : "26px 36px",
            textAlign: "center",
          }}>
            <p style={{ margin: 0, fontSize: isMobile ? 15 : 16.5, lineHeight: 1.6, color: "rgba(255,255,255,0.85)" }}>
              HelixScan lo convierte en un flujo automático: cada hallazgo fechado, clasificado
              y <strong style={{ color: "#fff" }}>trazable a la fuente oficial</strong>.
            </p>
          </div>
        </div>
      </section>

      {/* ── La plataforma (cards de cristal) ────────────────────────────── */}
      <section id="producto" style={{ position: "relative", overflow: "hidden" }}>
        <Glow top="15%" left="12%" size={600} color="rgba(8,145,178,0.22)" opacity={0.7} />
        <Glow top="80%" left="88%" size={560} color="rgba(124,58,237,0.16)" opacity={0.6} />
        <div style={{ ...container, position: "relative", padding: isMobile ? "68px 20px" : "112px 40px", textAlign: "center" }}>
          <h2 className="lp-reveal" style={h2}>
            Todo lo que tu equipo necesita<br />para no llegar tarde.
          </h2>
          <div style={{
            display: "grid", gap: isMobile ? 14 : 18, marginTop: isMobile ? 40 : 60,
            gridTemplateColumns: isMobile ? "1fr" : isTablet ? "1fr 1fr" : "repeat(3, 1fr)",
          }}>
            {FEATURES.map((f, i) => (
              <div key={f.title} className="lp-glass lp-card lp-reveal" style={{
                padding: isMobile ? "26px 22px" : "32px 28px", textAlign: "left",
                transitionDelay: `${(i % 3) * 0.07}s`,
              }}>
                <div style={{
                  width: 46, height: 46, borderRadius: 23,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: "rgba(8,145,178,0.1)", border: "1px solid rgba(8,145,178,0.2)",
                  marginBottom: 18,
                }}>
                  <Ic d={f.icon} />
                </div>
                <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em", margin: "0 0 10px", color: INK }}>{f.title}</h3>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: MUTED }}>{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cómo funciona ───────────────────────────────────────────────── */}
      <section style={{ position: "relative" }}>
        <div style={{ ...container, padding: isMobile ? "60px 20px" : "96px 40px", textAlign: "center" }}>
          <h2 className="lp-reveal" style={h2}>Operativo el mismo día.</h2>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 14 : 18, marginTop: isMobile ? 40 : 60 }}>
            {[
              { n: "1", t: "Alta guiada en 3 minutos",
                b: "Buscas tu laboratorio en el catálogo oficial CIMA, eliges tus productos y la plataforma te sugiere la competencia de cada molécula." },
              { n: "2", t: "Monitorización continua",
                b: "Sondeo de las fuentes oficiales cada 30 minutos en horario laboral. Cada hallazgo, fechado y enlazado a su documento original." },
              { n: "3", t: "Tu equipo decide",
                b: "Alertas urgentes cuando importa, radar mensual para el comité, comparador para negociar e informe ejecutivo para dirección." },
            ].map((s, i) => (
              <div key={s.n} className="lp-glass lp-card lp-reveal" style={{
                padding: isMobile ? "26px 22px" : "34px 30px", textAlign: "left",
                transitionDelay: `${i * 0.08}s`,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 20, marginBottom: 18,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  background: TEAL, color: "#fff", fontSize: 17, fontWeight: 700,
                  boxShadow: "0 4px 16px -6px rgba(8,145,178,0.5)",
                }}>{s.n}</div>
                <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.015em", margin: "0 0 10px", color: INK }}>{s.t}</h3>
                <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.65, color: MUTED }}>{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Planes ──────────────────────────────────────────────────────── */}
      <section id="planes" style={{ position: "relative", overflow: "hidden" }}>
        <Glow top="60%" left="50%" size={800} color="rgba(8,145,178,0.16)" opacity={0.7} />
        <div style={{ ...container, position: "relative", padding: isMobile ? "68px 20px" : "112px 40px", textAlign: "center" }}>
          <h2 className="lp-reveal" style={h2}>Un plan para cada equipo.</h2>
          <p className="lp-reveal" style={sectionSub}>
            Sin permanencia y con alta guiada incluida. Cuéntanos cómo trabaja tu equipo
            y te recomendamos el punto de partida.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: narrow ? "1fr" : "repeat(3, 1fr)", gap: isMobile ? 16 : 20, marginTop: isMobile ? 40 : 60, alignItems: "stretch" }}>
            {PLANS.map((p, i) => (
              <div key={p.id} className={`lp-reveal lp-card ${p.pro ? "lp-glass-strong" : "lp-glass"}`} style={{
                padding: isMobile ? "28px 24px" : "36px 30px",
                display: "flex", flexDirection: "column", textAlign: "left",
                transitionDelay: `${i * 0.07}s`,
                ...(p.pro ? { border: "1px solid rgba(8,145,178,0.45)", boxShadow: "0 24px 80px -24px rgba(8,145,178,0.35), inset 0 1px 0 rgba(255,255,255,0.95)" } : {}),
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em", color: INK }}>{p.id}</div>
                  {p.pro && (
                    <span style={{
                      fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
                      color: "#fff", background: TEAL, borderRadius: 999, padding: "5px 12px",
                    }}>Recomendado</span>
                  )}
                </div>
                <div style={{ fontSize: 13.5, color: MUTED, margin: "6px 0 24px" }}>{p.tagline}</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                  {p.features.map(f => (
                    <div key={f} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <span style={{ flexShrink: 0, marginTop: 2 }}><Ic d={PATHS.check} size={15} /></span>
                      <span style={{ fontSize: 14, color: "rgba(11,18,21,0.78)", lineHeight: 1.55 }}>{f}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => setContactOpen(true)}
                        className={`lp-btn ${p.pro ? "lp-btn-primary" : "lp-btn-glass"}`}
                        style={{ marginTop: 28, width: "100%", fontSize: 14.5, padding: "14px 20px" }}>
                  Hablar con el equipo
                </button>
              </div>
            ))}
          </div>
          <p className="lp-reveal" style={{ fontSize: 13, color: FAINT, marginTop: 26 }}>
            ¿SSO, contrato a medida o más usuarios? Lo vemos en la reunión.
          </p>
        </div>
      </section>

      {/* ── CTA final ───────────────────────────────────────────────────── */}
      <section style={{ position: "relative", overflow: "hidden" }}>
        <Glow top="50%" left="50%" size={760} color="rgba(8,145,178,0.22)" opacity={0.8} />
        <div className="lp-reveal" style={{ ...container, position: "relative", padding: isMobile ? "80px 20px 90px" : "132px 40px 140px", textAlign: "center" }}>
          <h2 style={{ ...h2, fontSize: isMobile ? 38 : 62 }}>
            ¿Hablamos <span style={{ color: TEAL }}>30 minutos</span>?
          </h2>
          <p style={sectionSub}>
            Te enseñamos la plataforma con datos reales del mercado español y, si encaja,
            tu portfolio queda configurado esa misma semana.
          </p>
          <button className="lp-btn lp-btn-primary" style={{ fontSize: 16.5, padding: "18px 36px", marginTop: 36 }}
                  onClick={() => setContactOpen(true)}>
            Solicitar una reunión
          </button>
          <div style={{ marginTop: 20, fontSize: 13, color: FAINT }}>
            Sin permanencia · Alta guiada incluida · Respuesta en menos de 24 h
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer style={{ borderTop: "1px solid rgba(11,18,21,0.08)" }}>
        <div style={{
          ...container, padding: isMobile ? "24px 20px" : "28px 40px",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12,
        }}>
          <span style={{ fontSize: 13, color: FAINT }}>
            © {new Date().getFullYear()} HelixScan · una solución de{" "}
            <a href="https://haltrixpartners.com" target="_blank" rel="noopener"
               style={{ color: FAINT, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
              Haltrix Partners
            </a>
          </span>
          <span style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <a href={MAILTO} className="lp-navlink" style={{ fontSize: 13 }}>Contacto</a>
            <Link to="/cookies" className="lp-navlink" style={{ fontSize: 13 }}>Política de cookies</Link>
            <Link to="/login" className="lp-navlink" style={{ fontSize: 13 }}>Acceso clientes</Link>
          </span>
        </div>
      </footer>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
}
